import { ReactElement, useState, useRef } from 'react';
import { FORM_CONFIGS } from './util/feedbackForm';
import { Button } from '@/components/Button';
import { useTranslation } from 'react-i18next';

const FeedbackForm = (): ReactElement => {
  const [step, setStep] = useState<'radio' | 'textarea' | 'thanks'>('radio');
  const [radioValue, setRadioValue] = useState<string>('');
  const [textareaValue, setTextareaValue] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { t } = useTranslation('analysis');
  const questionLabel = t('feedback.question');
  const instructionLabel = t('feedback.instructions');
  const yesLabel = t('feedback.options.yes');
  const noLabel = t('feedback.options.no');
  const submitLabel = t('feedback.submit');
  const thanksLabel = t('feedback.thanks');

  // Googleフォーム送信用のhidden iframe
  const hiddenIframeName = 'hidden_iframe';

  // ラジオボタン送信
  const handleRadioChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRadioValue(value);

    // Googleフォームに送信
    const formData = new FormData();
    formData.append(FORM_CONFIGS.FORM1.ENTRIES.RESPONSE, value);
    formData.append(FORM_CONFIGS.FORM1.ENTRIES.REFERRER, window.location.href);
    formData.append(FORM_CONFIGS.FORM1.ENTRIES.USER_AGENT, navigator.userAgent);

    await fetch(FORM_CONFIGS.FORM1.ACTION_URL, {
      method: 'POST',
      mode: 'no-cors',
      body: formData,
    });

    setStep('textarea');
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
  };

  // テキストエリア送信
  const handleTextareaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!textareaValue) return;

    const formData = new FormData();
    formData.append(FORM_CONFIGS.FORM2.ENTRIES.FEEDBACK, textareaValue);

    await fetch(FORM_CONFIGS.FORM2.ACTION_URL, {
      method: 'POST',
      mode: 'no-cors',
      body: formData,
    });

    setStep('thanks');
  };

  return (
    <div id="feedbackform" className="w-full mt-0 text-center">
      <div className="min-h-60 lg:min-h-60 m-6 mt-0 p-6 pt-16 lg:pt-16 text-center border rounded-lg">
        {step === 'radio' && (
          <form className="lg:w-6/12 mx-auto">
            <p className="mb-6 text-lg font-semibold">{questionLabel}</p>
            <div className="flex justify-center gap-8">
              <label className="block cursor-pointer border border-blue-500 text-blue-500 rounded py-2 px-4 hover:bg-blue-50 transition">
                <input
                  type="radio"
                  id="radio1"
                  name={FORM_CONFIGS.FORM1.ENTRIES.RESPONSE}
                  value="はい"
                  className="hidden peer"
                  checked={radioValue === 'はい'}
                  onChange={handleRadioChange}
                />
                <span className="peer-checked:font-bold">{yesLabel}</span>
              </label>
              <label className="block cursor-pointer border border-blue-500 text-blue-500 rounded py-2 px-4 hover:bg-blue-50 transition">
                <input
                  type="radio"
                  id="radio2"
                  name={FORM_CONFIGS.FORM1.ENTRIES.RESPONSE}
                  value="いいえ"
                  className="hidden peer"
                  checked={radioValue === 'いいえ'}
                  onChange={handleRadioChange}
                />
                <span className="peer-checked:font-bold">{noLabel}</span>
              </label>
            </div>
          </form>
        )}
        {step === 'textarea' && (
          <form onSubmit={handleTextareaSubmit} className="lg:w-6/12 mx-auto">
            <p className="mb-3">{instructionLabel}</p>
            <textarea
              ref={textareaRef}
              name={FORM_CONFIGS.FORM2.ENTRIES.FEEDBACK}
              required
              className="form-control mb-5 w-full border rounded p-2"
              rows={3}
              value={textareaValue}
              onChange={(e) => setTextareaValue(e.target.value)}
            />
            <Button type="submit" variant="primary" className="w-full">
              {submitLabel}
            </Button>
          </form>
        )}
        {step === 'thanks' && (
          <div className="text-lg font-semibold py-8">{thanksLabel}</div>
        )}
        {/* Googleフォーム送信用のhidden iframe（レガシー対応用、実際にはfetchで送信） */}
        <iframe name={hiddenIframeName} id={hiddenIframeName} style={{ display: 'none' }} />
      </div>
    </div>
  );
};

export default FeedbackForm;
